import type {
    ProcessDiagramPositionType,
    ProcessExecuteIntervalType,
    ProcessExecuteIntervalUnit,
    ProcessIntervalAnchor,
    ProcessNextTaskType,
    ProcessTemplateProcessDataType,
    ProcessTemplateTaskType,
} from '@econtractor/types';
import { PROCESS_EXECUTE_INTERVAL_UNITS, PROCESS_INTERVAL_ANCHORS } from '@econtractor/types';

const INTERVAL_UNITS: ProcessExecuteIntervalUnit[] = [...PROCESS_EXECUTE_INTERVAL_UNITS];
const INTERVAL_ANCHORS: ProcessIntervalAnchor[] = [...PROCESS_INTERVAL_ANCHORS];

export const processTaskInstanceKey = (templateId: number, dtId: number): string => `${templateId}:${dtId}`;

export const parseProcessTaskInstanceKey = (
    raw: string | null | undefined,
): { templateId: number; dtId: number } | null => {
    if (!raw) return null;
    const sep = raw.indexOf(':');
    if (sep < 1) return null;
    const templateId = Number(raw.slice(0, sep));
    const dtId = Number(raw.slice(sep + 1));
    if (!Number.isFinite(templateId) || templateId <= 0 || !Number.isFinite(dtId) || dtId <= 0) return null;
    return { templateId, dtId };
};

export const isSameProcessTaskInstance = (
    task: Pick<ProcessTemplateTaskType, 'templateId' | 'dtId'>,
    templateId: number,
    dtId: number,
): boolean => task.templateId === templateId && task.dtId === dtId;

const parseInterval = (raw: unknown): ProcessExecuteIntervalType | null => {
    if (raw == null || typeof raw !== 'object' || Array.isArray(raw)) return null;
    const rec = raw as Record<string, unknown>;
    if (typeof rec.unit !== 'string' || !INTERVAL_UNITS.includes(rec.unit as ProcessExecuteIntervalUnit)) return null;
    if (rec.unit === 'byCoreTask') return { value: 0, unit: 'byCoreTask' };
    const value = Number(rec.value);
    if (!Number.isInteger(value) || value < 0) return null;
    return { value, unit: rec.unit as ProcessExecuteIntervalUnit };
};

const parseIntervalAnchor = (raw: unknown): ProcessIntervalAnchor | null => {
    if (typeof raw !== 'string' || !INTERVAL_ANCHORS.includes(raw as ProcessIntervalAnchor) || raw !== 'deadline') {
        return null;
    }
    return 'deadline';
};

const parseNextTaskItem = (raw: unknown): ProcessNextTaskType | null => {
    if (raw == null || typeof raw !== 'object' || Array.isArray(raw)) return null;
    const rec = raw as Record<string, unknown>;
    const templateId = Number(rec.templateId);
    const dtId = Number(rec.dtId);
    if (!Number.isFinite(templateId) || templateId <= 0 || !Number.isFinite(dtId) || dtId <= 0) return null;
    const answerKey = rec.answerKey == null ? null : Number(rec.answerKey);
    if (answerKey != null && !Number.isFinite(answerKey)) return null;
    const interval = parseInterval(rec.interval);
    const intervalAnchor = parseIntervalAnchor(rec.intervalAnchor);
    return intervalAnchor
        ? { templateId, dtId, answerKey, interval, intervalAnchor }
        : { templateId, dtId, answerKey, interval };
};

export const getProcessNextTasks = (processData: unknown): ProcessNextTaskType[] => {
    if (processData == null || typeof processData !== 'object' || Array.isArray(processData)) return [];
    const rec = processData as ProcessTemplateProcessDataType;
    if (!Array.isArray(rec.nextTask)) return [];
    return rec.nextTask.map(parseNextTaskItem).filter((item): item is ProcessNextTaskType => item != null);
};

export const getProcessPosition = (processData: unknown): ProcessDiagramPositionType | null => {
    if (processData == null || typeof processData !== 'object' || Array.isArray(processData)) return null;
    const raw = (processData as Record<string, unknown>).position;
    if (raw == null || typeof raw !== 'object' || Array.isArray(raw)) return null;
    const pos = raw as Record<string, unknown>;
    const x = Number(pos.x);
    const y = Number(pos.y);
    if (!Number.isFinite(x) || !Number.isFinite(y)) return null;
    return { x, y };
};

const toProcessData = (
    nextTask: ProcessNextTaskType[],
    position: ProcessDiagramPositionType | null,
): ProcessTemplateProcessDataType | null => {
    if (nextTask.length === 0 && position == null) return null;
    return { nextTask, position };
};

export const setTaskNextTasks = (
    task: ProcessTemplateTaskType,
    nextTask: ProcessNextTaskType[],
): ProcessTemplateTaskType => ({
    ...task,
    processData: toProcessData(nextTask, getProcessPosition(task.processData)),
});

export const setTaskPosition = (
    task: ProcessTemplateTaskType,
    position: ProcessDiagramPositionType,
): ProcessTemplateTaskType => ({
    ...task,
    processData: toProcessData(getProcessNextTasks(task.processData), position),
});

export type ProcessNextTaskKey = {
    templateId: number;
    dtId: number;
    answerKey: number | null;
};

const isSameNextTask = (item: ProcessNextTaskType, key: ProcessNextTaskKey): boolean =>
    item.templateId === key.templateId && item.dtId === key.dtId && item.answerKey === key.answerKey;

export const addTaskNextTask = (task: ProcessTemplateTaskType, item: ProcessNextTaskType): ProcessTemplateTaskType => {
    const current = getProcessNextTasks(task.processData);
    if (current.some((link) => isSameNextTask(link, item))) return task;
    return setTaskNextTasks(task, [...current, item]);
};

export const removeTaskNextTask = (
    task: ProcessTemplateTaskType,
    key: ProcessNextTaskKey,
): ProcessTemplateTaskType =>
    setTaskNextTasks(
        task,
        getProcessNextTasks(task.processData).filter((link) => !isSameNextTask(link, key)),
    );

export const setTaskNextTaskInterval = (
    task: ProcessTemplateTaskType,
    key: ProcessNextTaskKey,
    interval: ProcessExecuteIntervalType | null,
    intervalAnchor: ProcessIntervalAnchor | null = null,
): ProcessTemplateTaskType =>
    setTaskNextTasks(
        task,
        getProcessNextTasks(task.processData).map((link) => {
            if (!isSameNextTask(link, key)) return link;
            const next: ProcessNextTaskType = { ...link, interval };
            delete next.intervalAnchor;
            if (intervalAnchor === 'deadline' && interval?.unit !== 'byCoreTask') next.intervalAnchor = 'deadline';
            return next;
        }),
    );

export const pruneProcessNextTasks = (
    tasks: ProcessTemplateTaskType[],
    removed: Pick<ProcessTemplateTaskType, 'templateId' | 'dtId'>,
): ProcessTemplateTaskType[] =>
    tasks.map((task) =>
        setTaskNextTasks(
            task,
            getProcessNextTasks(task.processData).filter(
                (link) => link.templateId !== removed.templateId || link.dtId !== removed.dtId,
            ),
        ),
    );

export const remapProcessNextTaskInstance = (
    tasks: ProcessTemplateTaskType[],
    from: Pick<ProcessTemplateTaskType, 'templateId' | 'dtId'>,
    to: Pick<ProcessTemplateTaskType, 'templateId' | 'dtId'>,
): ProcessTemplateTaskType[] => {
    if (from.templateId === to.templateId && from.dtId === to.dtId) return tasks;
    return tasks.map((task) =>
        setTaskNextTasks(
            task,
            getProcessNextTasks(task.processData).map((link) =>
                link.templateId === from.templateId && link.dtId === from.dtId
                    ? { ...link, templateId: to.templateId, dtId: to.dtId }
                    : link,
            ),
        ),
    );
};

export type ProcessIncomingTrigger = {
    templateId: number;
    dtId: number;
    taskName: string;
    answerKey: number | null;
    answerText: string | null;
    interval: ProcessExecuteIntervalType | null;
    intervalAnchor?: ProcessIntervalAnchor;
};

/** Вхідні ребра: яка задача запускає цей крок і з якої відповіді. */
export const getIncomingProcessTriggers = (
    task: Pick<ProcessTemplateTaskType, 'templateId' | 'dtId'>,
    tasks: ProcessTemplateTaskType[],
): ProcessIncomingTrigger[] => {
    const triggers: ProcessIncomingTrigger[] = [];
    for (const source of tasks) {
        for (const link of getProcessNextTasks(source.processData)) {
            if (!isSameProcessTaskInstance(task, link.templateId, link.dtId)) continue;
            const answer = link.answerKey == null
                ? null
                : (source.answers || []).find((item) => item.key === link.answerKey) ?? null;
            triggers.push({
                templateId: source.templateId,
                dtId: source.dtId,
                taskName: source.taskName,
                answerKey: link.answerKey,
                answerText: answer?.text ?? null,
                interval: link.interval,
                ...(link.intervalAnchor === 'deadline' ? { intervalAnchor: 'deadline' as const } : {}),
            });
        }
    }
    return triggers;
};

/** Вхідні ребра: інтервал запуску цієї задачі. null на ребрі — одразу. */
export const getIncomingProcessIntervals = (
    task: Pick<ProcessTemplateTaskType, 'templateId' | 'dtId'>,
    tasks: ProcessTemplateTaskType[],
): Array<{ interval: ProcessExecuteIntervalType | null; intervalAnchor?: ProcessIntervalAnchor }> => {
    const intervals: Array<{ interval: ProcessExecuteIntervalType | null; intervalAnchor?: ProcessIntervalAnchor }> = [];
    for (const source of tasks) {
        for (const link of getProcessNextTasks(source.processData)) {
            if (!isSameProcessTaskInstance(task, link.templateId, link.dtId)) continue;
            intervals.push(link.intervalAnchor === 'deadline'
                ? { interval: link.interval, intervalAnchor: 'deadline' }
                : { interval: link.interval });
        }
    }
    return intervals;
};

export const getProcessRootTasks = <T extends ProcessTemplateTaskType>(tasks: T[]): T[] => {
    const targets = new Set<string>();
    for (const task of tasks) {
        for (const link of getProcessNextTasks(task.processData)) {
            targets.add(processTaskInstanceKey(link.templateId, link.dtId));
        }
    }
    return tasks.filter((task) => !targets.has(processTaskInstanceKey(task.templateId, task.dtId)));
};
